import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Clock,
  FileText,
  Stethoscope,
  Activity,
  HeartPulse,
  Truck,
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  Zap,
  Award,
  Users,
  ChevronRight,
  Pill
} from 'lucide-react';

const serviceData = [
  { 
    id: 1,
    src: require('../../assets/image 6.jpeg'), 
    title: '24/7 Emergency Pharmacy', 
    category: 'Emergency',
    icon: Clock,
    badgeColor: 'bg-rose-50 text-rose-700 border-rose-200/80',
    iconBg: 'bg-rose-500/10 text-rose-600',
    desc: 'Round-the-clock availability for critical medications, first aid, and emergency medical supplies.' 
  },
  { 
    id: 2,
    src: require('../../assets/image 7.jpeg'), 
    title: 'Prescription Refills', 
    category: 'Prescriptions',
    icon: FileText,
    badgeColor: 'bg-emerald-50 text-emerald-700 border-emerald-200/80',
    iconBg: 'bg-emerald-500/10 text-emerald-600',
    desc: 'Fast, accurate, and hassle-free digitized prescription management and recurring medication refills.' 
  },
  { 
    id: 3,
    src: require('../../assets/image 8.jpeg'), 
    title: 'Pharmacist Consultations', 
    category: 'Advisory',
    icon: Stethoscope,
    badgeColor: 'bg-blue-50 text-blue-700 border-blue-200/80',
    iconBg: 'bg-blue-500/10 text-blue-600',
    desc: 'Personalized guidance on dosage, drug interactions, and health advice from certified pharmacists.' 
  },
  { 
    id: 4,
    src: require('../../assets/image9.jpeg'), 
    title: 'Medical & Surgical Devices', 
    category: 'Equipment',
    icon: Activity,
    badgeColor: 'bg-amber-50 text-amber-700 border-amber-200/80',
    iconBg: 'bg-amber-500/10 text-amber-600',
    desc: 'Reliable healthcare monitoring devices, BP monitors, glucometers, and authentic surgical aids.' 
  },
  { 
    id: 5,
    src: require('../../assets/image 10.jpeg'), 
    title: 'Chronic & Specialized Care', 
    category: 'Special Care',
    icon: HeartPulse,
    badgeColor: 'bg-purple-50 text-purple-700 border-purple-200/80',
    iconBg: 'bg-purple-500/10 text-purple-600',
    desc: 'Dedicated inventory for diabetes, cardiovascular, oncology, and rare chronic condition care.' 
  },
  { 
    id: 6,
    src: require('../../assets/image11.jpeg'), 
    title: 'Doorstep Express Delivery', 
    category: 'Delivery',
    icon: Truck,
    badgeColor: 'bg-teal-50 text-teal-700 border-teal-200/80',
    iconBg: 'bg-teal-500/10 text-teal-600',
    desc: 'Rapid and temperature-controlled home delivery right to your doorstep with real-time tracking.' 
  },
  { 
    id: 7,
    src: require('../../assets/image12.jpeg'), 
    title: '100% Genuine Quality Guarantee', 
    category: 'Assurance',
    icon: ShieldCheck,
    badgeColor: 'bg-indigo-50 text-indigo-700 border-indigo-200/80',
    iconBg: 'bg-indigo-500/10 text-indigo-600',
    desc: 'Direct sourcing from licensed pharmaceutical manufacturers ensures 100% authentic medicines.' 
  },
];

const features = [
  {
    icon: ShieldCheck,
    title: 'Authentic & Verified',
    description: 'Every product is 100% genuine with direct batch sourcing and quality checks.'
  },
  {
    icon: Zap,
    title: 'Instant Order Fulfillment',
    description: 'Rapid dispatch and organized inventory ensures zero waiting time.'
  },
  {
    icon: Award,
    title: 'Licensed Pharmacists',
    description: 'Expert guidance on prescriptions, dietary supplements, and precautions.'
  },
  {
    icon: Clock,
    title: 'Always Available',
    description: 'Ready to serve your healthcare needs all 365 days of the year.'
  }
];

const Services = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const [activeCategory, setActiveCategory] = useState('All');

  const categories = ['All', 'Emergency', 'Prescriptions', 'Advisory', 'Equipment', 'Delivery'];

  const filteredServices = isHomePage
    ? serviceData
    : activeCategory === 'All'
    ? serviceData
    : serviceData.filter(s => s.category === activeCategory);

  // Common card renderer
  const renderCard = (service, index) => {
    const IconComp = service.icon;

    return (
      <div 
        key={service.id || index} 
        className={`
          ${isHomePage 
            ? 'flex-none w-[290px] sm:w-[330px] md:w-[360px] lg:w-[380px] snap-start' 
            : 'w-full'
          }
          group
        `}
      >
        <div className="relative flex flex-col h-full bg-white rounded-3xl overflow-hidden border border-slate-200/80 shadow-sm hover:shadow-2xl hover:shadow-emerald-900/10 hover:border-emerald-300/80 transition-all duration-500 hover:-translate-y-1.5">
          
          {/* Top Image Container */}
          <div className="relative h-48 sm:h-52 md:h-56 overflow-hidden bg-slate-100">
            <img 
              src={service.src} 
              alt={service.title} 
              className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700 ease-out"
              loading="lazy"
            />
            {/* Subtle Gradient Shade */}
            <div className="absolute inset-0 bg-gradient-to-t from-slate-950/70 via-slate-950/20 to-transparent"></div>
            
            {/* Category Badge */}
            <div className="absolute top-3.5 left-3.5 flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/90 backdrop-blur-md border border-white/50 text-[11px] font-bold text-slate-800 shadow-sm">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
              {service.category}
            </div>

            {/* Quick Icon Float */}
            <div className="absolute bottom-3 right-3.5 w-10 h-10 rounded-2xl bg-white/90 backdrop-blur-md flex items-center justify-center text-emerald-700 shadow-lg border border-white/60 transform group-hover:rotate-12 group-hover:scale-110 transition-all duration-300">
              <IconComp className="w-5 h-5" />
            </div>
          </div>
          
          {/* Card Body */}
          <div className="p-5 sm:p-6 flex flex-col flex-grow justify-between bg-white">
            <div>
              <h3 className="text-lg font-bold text-slate-900 group-hover:text-emerald-700 transition-colors duration-300 line-clamp-1 mb-2">
                {service.title}
              </h3>
              <p className="text-slate-600 text-xs sm:text-sm leading-relaxed line-clamp-3">
                {service.desc}
              </p>
            </div>

            {/* Bottom Action Row */}
            <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
              <Link
                to="/contact"
                className="inline-flex items-center gap-1.5 text-xs font-bold text-emerald-600 group-hover:text-emerald-700 transition-colors"
              >
                <span>Inquire Service</span>
                <ChevronRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
              </Link>

              <div className="flex items-center gap-1 text-[11px] font-semibold text-slate-400">
                <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                <span>Available</span>
              </div>
            </div>
          </div>
          
        </div>
      </div>
    );
  };

  return (
    <section className="bg-gradient-to-b from-slate-50 via-white to-slate-50 py-16 sm:py-20 border-t border-slate-200/70 relative overflow-hidden">
      
      {/* Decorative ambient background glows */}
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-emerald-100/50 rounded-full blur-3xl -z-10 pointer-events-none"></div>
      <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-teal-100/40 rounded-full blur-3xl -z-10 pointer-events-none"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header Section */}
        <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight">
            Our Services
          </h2>
        </div>

        {/* Category Filters (Only on standalone /services page) */}
        {!isHomePage && (
          <div className="flex items-center justify-center flex-wrap gap-2 sm:gap-3 mb-10 sm:mb-12">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-xl text-xs sm:text-sm font-bold transition-all duration-300 ${
                  activeCategory === cat
                    ? 'bg-emerald-600 text-white shadow-md shadow-emerald-600/30 scale-105'
                    : 'bg-white text-slate-600 hover:text-emerald-700 hover:bg-emerald-50 border border-slate-200/80'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
        )}
        
        {/* Cards Container */}
        <div className={
          isHomePage 
            ? "flex overflow-x-auto gap-5 sm:gap-6 md:gap-7 pb-8 snap-x snap-mandatory hide-scrollbar" 
            : "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8 pb-10"
        }>
          {filteredServices.map((service, index) => renderCard(service, index))}
        </div>

        {/* Home Page "View All" CTA */}
        {isHomePage && (
          <div className="text-center mt-6">
            <Link 
              to="/services" 
              onClick={() => window.scrollTo(0,0)} 
              className="group inline-flex items-center gap-3 px-8 py-3.5 bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-sm sm:text-base rounded-2xl shadow-lg shadow-emerald-600/25 hover:shadow-emerald-600/40 hover:-translate-y-0.5 transition-all duration-300"
            >
              <span>Explore All Our Healthcare Services</span>
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1.5 transition-transform" />
            </Link>
          </div>
        )}

        {/* Why Choose Us Feature Grid (On /services page) */}
        {!isHomePage && (
          <div className="mt-16 sm:mt-24 pt-16 border-t border-slate-200/80">
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-xs font-bold text-emerald-600 uppercase tracking-wider">
                Why Patients Trust Us
              </span>
              <h3 className="text-2xl sm:text-3xl font-extrabold text-slate-900 mt-2">
                Uncompromising Quality in Every Dose
              </h3>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {features.map((feat, idx) => {
                const FeatIcon = feat.icon;
                return (
                  <div 
                    key={idx} 
                    className="p-6 bg-white rounded-2xl border border-slate-200/80 shadow-sm hover:shadow-xl hover:border-emerald-200 hover:-translate-y-1 transition-all duration-300"
                  >
                    <div className="w-12 h-12 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center mb-4">
                      <FeatIcon className="w-6 h-6" />
                    </div>
                    <h4 className="text-base font-bold text-slate-900 mb-2">
                      {feat.title}
                    </h4>
                    <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                      {feat.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {/* Stats Section */}
        {!isHomePage && (
          <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-6">
            <div className="p-6 bg-white rounded-2xl border border-slate-200/80 text-center shadow-sm">
              <div className="text-3xl sm:text-4xl font-black text-emerald-600">7+</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-600 mt-1">Core Healthcare Services</div>
            </div>
            <div className="p-6 bg-white rounded-2xl border border-slate-200/80 text-center shadow-sm">
              <div className="text-3xl sm:text-4xl font-black text-emerald-600">100%</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-600 mt-1">Authentic Medications</div>
            </div>
            <div className="p-6 bg-white rounded-2xl border border-slate-200/80 text-center shadow-sm">
              <div className="text-3xl sm:text-4xl font-black text-emerald-600">24/7</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-600 mt-1">Emergency Support</div>
            </div>
            <div className="p-6 bg-white rounded-2xl border border-slate-200/80 text-center shadow-sm">
              <div className="text-3xl sm:text-4xl font-black text-emerald-600">4.9★</div>
              <div className="text-xs sm:text-sm font-semibold text-slate-600 mt-1">Patient Satisfaction</div>
            </div>
          </div>
        )}

      </div>
    </section>
  );
};

export default Services;

